/**
 * ConciergeLayout — MS45 chrome cutover.
 *
 * The single chrome that wraps every authed customer-portal route.
 * Replaces the old AppLayout (navy sidebar + top header bar + flat
 * nav) which only Dashboard had successfully rebranded with the
 * concierge-v1 prototype's white-sidebar + Newsreader-wordmark
 * pattern. Every other authed page (Team, Calendar, Orders,
 * Settings, Billing, Notifications, Wishlist) was still inheriting
 * the old chrome — leaving a jarring "Dashboard is the new app, but
 * the rest is the old portal" feel that Lucky correctly called out.
 *
 * This component is the canonical chrome for the entire authed
 * customer experience. It owns:
 *   - 240px white sidebar with Newsreader italic "lembra" wordmark
 *     (was 220px pre-MS581; widened to match the Linear / Stripe /
 *     Notion / Vercel 240–256px sidebar convention identified by the
 *     Phase 4 competitive scan).
 *   - Two nav sections: Concierge (Up next, Team, Orders, Saved)
 *     and Settings (Cadence, Tier, Billing, Notifications)
 *   - Route-aware active state
 *   - Unread-notification badge wiring
 *   - "AutoPilot is on" status card at the bottom
 *   - Sign-out button
 *
 * The main area is a cream-bg <Outlet /> — pages render their full
 * content there. Dashboard renders its own list+detail grid inside
 * the outlet; every other page renders normally.
 *
 * Important: the SmartIntentBar that used to sit at the TOP of
 * Dashboard's main pane is NOT in this layout. Phase 4 of the
 * Phase 1-6 plan turns it into a floating Intercom-style widget
 * that lives on EVERY authed page rather than only Dashboard.
 * For Phase 1, the bar stays put inside Dashboard.tsx and gets
 * relocated when Phase 4 ships.
 */

import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { FloatingConcierge } from "@/components/concierge/FloatingConcierge";
import { FloatingConciergeProvider } from "@/contexts/FloatingConciergeContext";
// MS377: replace the Newsreader-italic text wordmark from MS45 with
// the canonical Logo SVG component (`/logo-navy.svg`). MS49's Logo
// component is the single source of truth for the brand mark across
// every surface — Pricing, BlogPost, Signup, BakeryLayout all use it.
// ConciergeLayout was the lone holdout still rendering the wordmark
// as a styled <span>, which made the dashboard sidebar visually
// inconsistent with the rest of the app per Lucky's call.
import { Logo } from "@/components/lembra/Logo";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  useActiveTeamMemberCount,
  useCompanySettings,
  useProfile,
  useUnreadNotificationCount,
  useWishlist,
} from "@/hooks/queries";
import { useNotificationGenerator } from "@/hooks/useNotificationGenerator";
import { usePrefetchDashboardTabs } from "@/hooks/usePrefetchDashboardTabs";
import { useCheckoutCancelledToast } from "@/hooks/useCheckoutCancelledToast";
// MS63a removed the MS51 ambient floating concierge widget; the
// Dashboard's inline SmartIntentBar → InlineConcierge replaces it.
// MS64e: AmbientConcierge.tsx itself deleted (was sitting dead in
// the components tree).

/* ─── Inline icons (calm 1.5px stroke, matches the prototype) ──────── */

function CalendarDotIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className={className}
    >
      <rect x="2" y="3" width="12" height="11" rx="1.5" />
      <path d="M2 6h12M5.5 2v2M10.5 2v2" />
    </svg>
  );
}
function PeopleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className={className}
    >
      <circle cx="6" cy="5" r="2.5" />
      <path d="M2 13c0-2.2 1.8-4 4-4s4 1.8 4 4" />
      <circle cx="11" cy="6" r="2" />
      <path d="M9 13c0-1.5 1-2.7 2.5-3" />
    </svg>
  );
}
// MS-cohesion: Orders nav row reads as "orders", not analytics. The
// prior HistoryIcon (a chart/zigzag glyph) implied a trends view; a
// receipt outline (notched footer + ruled lines) maps to the Orders
// route. 1.5px stroke + 16x16 viewBox keeps it in step with the rest
// of the inline rail icons.
function ReceiptIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3.5 2h9v12l-1.5-1-1.5 1-1.5-1-1.5 1-1.5-1L3.5 14z" />
      <path d="M6 5.5h4M6 8h4" />
    </svg>
  );
}
function HeartIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className={className}
    >
      <path d="M8 13.5s-5-3-5-7a3 3 0 0 1 5-2.2A3 3 0 0 1 13 6.5c0 4-5 7-5 7z" />
    </svg>
  );
}
function CardIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className={className}
    >
      <rect x="2" y="4" width="12" height="9" rx="1.5" />
      <path d="M2 7h12" />
    </svg>
  );
}
function BellIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className={className}
    >
      <path d="M3.5 11.5V7a4.5 4.5 0 1 1 9 0v4.5l1 1.5h-11z" />
      <path d="M6.5 14.5h3" />
    </svg>
  );
}
// MS530: Refer-a-friend now has its own nav row in the Growth section.
// Wrapped gift box reads instantly as "share a gift" — semantically
// distinct from HeartIcon (Saved / wishlist) and from the lucide-react
// Gift icon used in Orders' "Create order" CTA (which lives in main-
// content, not the sidebar). 1.5px stroke + 16x16 viewBox matches the
// rest of the inline nav icons.
function GiftIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className={className}
    >
      <rect x="2" y="7" width="12" height="7" rx="1" />
      <path d="M1.5 5h13v2h-13z" />
      <path d="M8 5v9" />
      <path d="M8 5s-1.5-2.5-3-2.5a1.5 1.5 0 0 0 0 3" />
      <path d="M8 5s1.5-2.5 3-2.5a1.5 1.5 0 0 1 0 3" />
    </svg>
  );
}
function CogIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className={className}
    >
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1.5v2M8 12.5v2M14.5 8h-2M3.5 8h-2M12.6 3.4l-1.4 1.4M4.8 11.2l-1.4 1.4M12.6 12.6l-1.4-1.4M4.8 4.8L3.4 3.4" />
    </svg>
  );
}

/* ─── Nav model ───────────────────────────────────────────────────── */

interface NavItemDef {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
}

// The Concierge group is "what the user came here to do" — the
// celebration workflow itself (Up next, the team, history, saved
// gifts). Order is left-to-right importance: most-used at the top.
const NAV_CONCIERGE: NavItemDef[] = [
  { label: "Up next", to: "/dashboard", icon: CalendarDotIcon },
  { label: "Team", to: "/team", icon: PeopleIcon },
  { label: "Orders", to: "/orders", icon: ReceiptIcon },
  { label: "Saved", to: "/wishlist", icon: HeartIcon },
];

// Referrals sit outside the celebration workflow but aren't account
// settings — grouped under "Share" (customer-facing), not internal
// "Growth" jargon.
export const NAV_GROWTH: NavItemDef[] = [
  { label: "Refer a friend", to: "/refer", icon: GiftIcon },
];

// The Settings group is the operating-the-account-itself work.
// Billing and Notifications are top-level routes; Settings is the
// single entry for cadence/tier/etc (Cadence is a SectionCard inside
// /settings, not its own nav row — see MS527 AUDIT-003 where the prior
// `/settings#cadence` row caused two rows to highlight simultaneously
// once the hash-stripping isActive comparison reduced both to `/settings`).
export const NAV_SETTINGS: NavItemDef[] = [
  { label: "Settings", to: "/settings", icon: CogIcon },
  { label: "Billing", to: "/billing", icon: CardIcon },
  { label: "Notifications", to: "/notifications", icon: BellIcon },
];

/**
 * MS527 AUDIT-003: pure helper for sidebar active-state matching.
 * Strips any trailing hash from the nav item's `to` before comparing
 * to the current pathname — the deep-link still works (NavLink owns
 * the scroll target) but the highlight stays on the path-matching row.
 * Exported so the regression test can pin the behaviour without
 * spinning up the full layout.
 */
export function isActiveRoute(currentPathname: string, to: string): boolean {
  const target = to.split("#")[0];
  if (target === "/settings") {
    return currentPathname === target || currentPathname.startsWith(`${target}/`);
  }
  return currentPathname === target;
}

/**
 * MS527 AUDIT-008: sidebar className constant. Extracted so the
 * regression test can assert the sticky-positioning classes are
 * present without rendering the whole layout. `md:sticky md:top-0
 * md:h-screen md:overflow-y-auto` together pin the sidebar to the
 * viewport so it doesn't scroll off on long pages like /settings;
 * `hidden md:flex` keeps the mobile-hidden behaviour unchanged.
 */
export const SIDEBAR_ASIDE_CLASSNAME =
  "hidden md:sticky md:top-0 md:flex md:h-screen md:overflow-y-auto flex-col gap-7 border-r bg-white px-4 py-6";

/* ─── Nav primitives ──────────────────────────────────────────────── */

function NavSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div
        className="px-2 pb-1.5 text-[10px] font-medium uppercase"
        style={{
          fontFamily: "JetBrains Mono, monospace",
          letterSpacing: "0.12em",
          color: "rgba(29,52,97,0.35)",
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function NavItem({
  item,
  active,
  count,
}: {
  item: NavItemDef;
  active: boolean;
  count?: number;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      // MS23: aria-current="page" so screen readers announce the
      // active surface; matches WAI-ARIA best practice for sidebar
      // nav.
      aria-current={active ? "page" : undefined}
      className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[13.5px] transition-colors ${
        active
          ? "bg-[rgba(29,52,97,0.06)] font-medium"
          : "hover:bg-[rgba(29,52,97,0.04)]"
      }`}
      style={{ color: "rgba(29,52,97,0.92)" }}
    >
      <Icon
        className={`h-3.5 w-3.5 flex-shrink-0 ${active ? "opacity-100" : "opacity-55"}`}
      />
      <span className="flex-1">{item.label}</span>
      {count !== undefined && count > 0 && (
        // 99+ cap so a power user with 100+ items doesn't break
        // the chip width. Same convention as Dashboard's prior
        // sidebar.
        <span
          className="text-[11px] tabular-nums"
          style={{ color: "rgba(29,52,97,0.35)" }}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </NavLink>
  );
}

/**
 * MS527 AUDIT-002: status pill at the foot of the sidebar reflects the
 * live `companies.concierge_autopilot_enabled` flag rather than the
 * pre-MS527 hardcoded "AutoPilot is on" string. States:
 *  - true  → "AutoPilot is on" with the green dot (current visual).
 *  - false → coral "Set up AutoPilot →" link to /settings/autopilot.
 *  - undefined (loading) → nothing rendered, so we don't flash either
 *    state during the first paint.
 * Exported so the regression test in src/test/ConciergeLayout.test.tsx
 * can render it directly without spinning up the whole layout.
 */
export function AutopilotPill({ enabled }: { enabled: boolean | null | undefined }) {
  if (enabled === undefined) return null;
  if (enabled === true) {
    return (
      <div
        className="mt-auto rounded-[10px] border p-3.5"
        style={{
          background: "#FFF8E7",
          borderColor: "rgba(29,52,97,0.08)",
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="block h-1.5 w-1.5 rounded-full"
            style={{
              background: "#5B8C76",
              boxShadow: "0 0 0 3px rgba(91,140,118,0.18)",
            }}
          />
          <span
            className="text-[12.5px] font-semibold"
            style={{ color: "rgba(29,52,97,0.92)" }}
          >
            AutoPilot is on
          </span>
        </div>
        <p
          className="mt-1.5 text-[11.5px] leading-[1.45]"
          style={{ color: "rgba(29,52,97,0.55)" }}
        >
          Recommendations land the day before each event. Edit window stays
          open until 9 am day-of.
        </p>
      </div>
    );
  }
  return (
    <NavLink
      to="/settings/autopilot"
      className="mt-auto rounded-[10px] border p-3.5 transition-colors hover:bg-[rgba(29,52,97,0.04)]"
      style={{
        background: "#FFF8E7",
        borderColor: "rgba(29,52,97,0.08)",
      }}
    >
      <span
        className="text-[12.5px] font-semibold"
        style={{ color: "#C66B5C" }}
      >
        Set up AutoPilot →
      </span>
      <p
        className="mt-1.5 text-[11.5px] leading-[1.45]"
        style={{ color: "rgba(29,52,97,0.55)" }}
      >
        Turn on AutoPilot to let recommendations land automatically the day
        before each event.
      </p>
    </NavLink>
  );
}

/* ─── Layout ─────────────────────────────────────────────────────── */

export function ConciergeLayout() {
  const { user } = useAuth();
  useCheckoutCancelledToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // MS251: mobile nav drawer state.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // MS251: auto-close the mobile drawer on route change (defense-in-
  // depth — the SheetContent's bubble-up onClick handler also closes
  // it, but this catches programmatic navigation too).
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Notification badge wiring — same approach as the old AppLayout so
  // the count refreshes on route change + on the custom "marked-read"
  // window event.
  const unreadQuery = useUnreadNotificationCount(user?.id);
  const unreadCount = unreadQuery.data ?? 0;

  // Counts for the inline badge chips on Team + Saved nav items.
  const teamCountQuery = useActiveTeamMemberCount(user?.id);
  const teamCount = teamCountQuery.data ?? 0;
  const wishlistQuery = useWishlist(user?.id);
  const savedCount = wishlistQuery.data?.length ?? 0;

  // MS527 AUDIT-002: read the live autopilot flag so the sidebar pill
  // reflects reality. Pre-MS527 the pill was a hardcoded "AutoPilot is
  // on" — toggling concierge_autopilot_enabled in /settings had no
  // visible effect here. Profile resolves company_id; companySettings
  // then reads concierge_autopilot_enabled scoped to that company.
  // `autopilotEnabled` is `undefined` until both queries finish, which
  // AutopilotPill renders as "nothing" to avoid flashing either state.
  const profileQuery = useProfile();
  const companyId = profileQuery.data?.company_id ?? null;
  // MS263: warm tab caches from any authed page — not only /dashboard —
  // so the first sidebar click doesn't pay a 3–4s cold-load tax.
  usePrefetchDashboardTabs(companyId, user?.id);
  const companySettingsQuery = useCompanySettings(companyId);
  const autopilotEnabled: boolean | null | undefined =
    companySettingsQuery.data === undefined
      ? undefined
      : (companySettingsQuery.data?.concierge_autopilot_enabled ?? false);

  const invalidateUnread = useCallback(() => {
    if (!user?.id) return;
    queryClient.invalidateQueries({
      queryKey: ["notifications", user.id, "unread-count"],
    });
  }, [queryClient, user?.id]);

  useNotificationGenerator(user?.id, invalidateUnread);

  // Refresh badge on route change so navigation always reconciles
  // the unread count without explicit invalidation calls.
  useEffect(() => {
    invalidateUnread();
  }, [location.pathname, invalidateUnread]);

  useEffect(() => {
    const handler = () => invalidateUnread();
    window.addEventListener("lembra:notifications-read", handler);
    return () => window.removeEventListener("lembra:notifications-read", handler);
  }, [invalidateUnread]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const isActive = useCallback(
    (to: string) => isActiveRoute(location.pathname, to),
    [location.pathname],
  );

  return (
    <FloatingConciergeProvider>
    <div
      className="min-h-screen"
      style={{
        background: "#FAF7F0",
        color: "rgba(29,52,97,0.92)",
        fontFamily: "DM Sans, sans-serif",
      }}
    >
      {/* MS250: responsive grid. Pre-MS250 this was a hardcoded
          `gridTemplateColumns: "220px 1fr"` with no breakpoint.
          Combined with Dashboard's inner `1fr 360px` grid, on
          tablet widths (<1024px) the list pane was squashed
          to ~150px wide — the root cause of Lucky's "everything
          stretches in and out of frame" report.

          MS581: sidebar widened 220px → 240px (Phase 4 foundation).
          The Phase 4 competitive scan found Linear (~240px expanded),
          Stripe (240–256px), Notion (240–280px), Vercel + Brex/Ramp
          (240–280px) all converged on 240px+ as the desktop sidebar
          width. At 220px Lembra's sidebar was tight enough that the
          "Refer a friend" + "Notifications" labels were brushing the
          right edge of the rail; 240px gives the label column 20px
          of fresh runway without affecting the main content area's
          breathing room on a 1280px laptop (1280 − 240 − 32 padding
          = 1008px usable, still room for max-w-5xl PageShell content
          with its own gutters).

          Now:
            mobile (sm and below): sidebar hidden entirely so the
              page content gets the full screen. Top mobile bar
              below provides logo + sign out fallback.
            md+: 240px + 1fr layout (was 220px pre-MS581).

          The mobile Sheet drawer below stays at 240px (unchanged —
          it was already at the new convention pre-MS581).

          Follow-up (MS582): a sidebar collapse shortcut a la Linear's
          `[` key. Out of scope for MS581. */}
      <div
        className="grid min-h-screen grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)]"
      >
        {/* ── Sidebar — hidden on mobile, shown md+ —
            see SIDEBAR_ASIDE_CLASSNAME for the sticky-pin rationale. */}
        <aside
          className={SIDEBAR_ASIDE_CLASSNAME}
          style={{ borderColor: "rgba(29,52,97,0.08)" }}
        >
          {/* MS377: canonical Logo SVG (replaces the MS45 Newsreader
              text wordmark). Logo is itself a <Link to="/dashboard">
              when the user is signed in, so no outer <Link> wrapper
              needed. h-8 (size="md") matches the previous wordmark's
              optical weight; default onDark=false renders the navy
              variant on the white sidebar. */}
          <Logo size="md" className="px-1.5" />

          <NavSection label="Concierge">
            {NAV_CONCIERGE.map((item) => (
              <NavItem
                key={item.label}
                item={item}
                active={isActive(item.to)}
                count={
                  item.label === "Team"
                    ? teamCount
                    : item.label === "Saved" && savedCount > 0
                      ? savedCount
                      : undefined
                }
              />
            ))}
          </NavSection>

          <NavSection label="Share">
            {NAV_GROWTH.map((item) => (
              <NavItem
                key={item.label}
                item={item}
                active={isActive(item.to)}
              />
            ))}
          </NavSection>

          <NavSection label="Settings">
            {NAV_SETTINGS.map((item) => (
              <NavItem
                key={item.label}
                item={item}
                active={isActive(item.to)}
                count={
                  item.label === "Notifications" && unreadCount > 0
                    ? unreadCount
                    : undefined
                }
              />
            ))}
          </NavSection>

          {/* MS527 AUDIT-002: live AutopilotPill — see component
              docstring. Pre-MS527 this was a hardcoded "AutoPilot is on"
              card that lied when the user toggled the flag off. */}
          <AutopilotPill enabled={autopilotEnabled} />

          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] hover:bg-[rgba(29,52,97,0.04)]"
            style={{ color: "rgba(29,52,97,0.55)" }}
          >
            <LogOut size={13} /> Sign out
          </button>
        </aside>

        {/* ── Main: <Outlet /> renders the page ── */}
        <main className="min-w-0">
          {/* MS250 + MS251: mobile top bar visible only when the
              sidebar is hidden (below md). MS251 adds a hamburger
              that opens the full nav inside a Sheet drawer, so
              mobile users can reach /team, /calendar, /orders, /settings
              without scraping inline links. The Sheet auto-closes
              when a nav item is clicked (handled by NavLink's natural
              route change + the explicit `setMobileNavOpen(false)`
              wired into the SheetContent's onClick capture). */}
          <div className="md:hidden flex items-center justify-between border-b bg-white px-3 py-2.5"
               style={{ borderColor: "rgba(29,52,97,0.08)" }}>
            <div className="flex items-center gap-1">
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <button
                    className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-[rgba(29,52,97,0.04)]"
                    aria-label="Open navigation menu"
                  >
                    <Menu size={18} className="text-navy" />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="w-[240px] p-0 bg-white"
                  onClick={(e) => {
                    // Close drawer when a nav link is tapped (event
                    // bubbles up from <NavLink> click). We close
                    // optimistically; the route change happens via
                    // react-router naturally.
                    if ((e.target as HTMLElement).closest("a")) {
                      setMobileNavOpen(false);
                    }
                  }}
                >
                  <div className="flex h-full flex-col gap-7 px-4 py-6">
                    {/* MS377: SVG logo in the mobile drawer too. */}
                    <Logo size="md" className="px-1.5" />
                    <NavSection label="Concierge">
                      {NAV_CONCIERGE.map((item) => (
                        <NavItem
                          key={item.label}
                          item={item}
                          active={isActive(item.to)}
                          count={
                            item.label === "Team"
                              ? teamCount
                              : item.label === "Saved" && savedCount > 0
                                ? savedCount
                                : undefined
                          }
                        />
                      ))}
                    </NavSection>
                    <NavSection label="Share">
                      {NAV_GROWTH.map((item) => (
                        <NavItem
                          key={item.label}
                          item={item}
                          active={isActive(item.to)}
                        />
                      ))}
                    </NavSection>
                    <NavSection label="Settings">
                      {NAV_SETTINGS.map((item) => (
                        <NavItem
                          key={item.label}
                          item={item}
                          active={isActive(item.to)}
                          count={
                            item.label === "Notifications" && unreadCount > 0
                              ? unreadCount
                              : undefined
                          }
                        />
                      ))}
                    </NavSection>
                    <button
                      onClick={() => {
                        setMobileNavOpen(false);
                        handleSignOut();
                      }}
                      className="mt-auto flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] hover:bg-[rgba(29,52,97,0.04)]"
                      style={{ color: "rgba(29,52,97,0.55)" }}
                    >
                      <LogOut size={13} /> Sign out
                    </button>
                  </div>
                </SheetContent>
              </Sheet>
              {/* MS377: SVG logo in the mobile top bar (was the smaller
                  22px text wordmark). size="sm" = h-6 (24px) keeps the
                  same optical scale next to the hamburger button. */}
              <Logo size="sm" className="ml-1" />
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[12.5px] hover:bg-[rgba(29,52,97,0.04)]"
              style={{ color: "rgba(29,52,97,0.55)" }}
              aria-label="Sign out"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
          <div key={location.pathname} className="page-enter">
            <Outlet />
          </div>
        </main>
      </div>

      {/* MS264: floating concierge — one persisted thread across authed
          routes (except /settings, /chat, /billing). Suppresses when
          DashboardConciergeDrawer or checkout modals own the surface. */}
      <FloatingConcierge />
    </div>
    </FloatingConciergeProvider>
  );
}
